import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Send, Loader2, Scale, BookMarked, ShieldCheck, FileText } from 'lucide-react';
import { api } from '../../api/client';
import { Can, useCan } from '../../lib/rbac';

interface InvoiceSummary {
  id: number;
  invoiceNumber: string;
  fromCompany: string;
  toCompany: string;
  quantity: number;
  unitPrice: number | null;
  totalAmount: number | null;
  invoiceDate: string;
  status: string;
  pdfReady: boolean;
  downloadPath: string;
}

const DEFENSE_START = 'BORRADOR DE DEFENSA:';
const ATTACH_INVOICE_RE = /ADJUNTAR FACTURA:\s*S[IÍ](?![A-Za-zÁ-ÿ])/i;

/** La marca solo vale FUERA de la carta (espejo de la regla del backend). */
export function wantsInvoiceAttached(text: string): boolean {
  const i = text.indexOf(DEFENSE_START);
  return ATTACH_INVOICE_RE.test(i >= 0 ? text.slice(0, i) : text);
}

/**
 * Chat "El Defensor" por denuncia PPPI (agente jurídico, Sonnet).
 * Dictamina defender/corregir/retirar/esperar con el expediente completo.
 * Si redacta una defensa (entre "BORRADOR DE DEFENSA:" y "FIN DEL BORRADOR"),
 * aparece el botón "Enviar esta defensa" — el envío SIEMPRE pasa por confirmación
 * acá, con vista previa del texto exacto que viaja al denunciante.
 */

interface DefMsg {
  id?: number;
  role: 'user' | 'assistant';
  content: string;
  saved_playbook?: { id: string; accion: string; text: string }[] | null;
}

const DRAFT_START_RE = /BORRADOR DE DEFENSA:\s*([\s\S]+)/;
const DRAFT_END_MARKER = 'FIN DEL BORRADOR';

/**
 * Extrae SOLO lo que está entre "BORRADOR DE DEFENSA:" y "FIN DEL BORRADOR".
 * Sin marca de fin NO hay borrador enviable (missingEnd) — así los comentarios
 * internos del Defensor jamás viajan en el PDF (incidente caso 37695060).
 */
export function extractDefenseDraft(text: string): {
  draft: string | null;
  missingEnd: boolean;
} {
  const m = text.match(DRAFT_START_RE);
  if (!m) return { draft: null, missingEnd: false };
  const idx = m[1].indexOf(DRAFT_END_MARKER);
  if (idx < 0) return { draft: null, missingEnd: true };
  const draft = m[1]
    .slice(0, idx)
    .replace(/^\s*(?:-{3,}\s*)+/, '')
    .trim();
  return { draft: draft.length >= 50 ? draft : null, missingEnd: false };
}

export function DefenderDrawer({
  sellerId,
  caseId,
  caseTitle,
  onSent,
  onClose,
}: {
  sellerId: number;
  caseId: string;
  caseTitle: string;
  onSent: () => void;
  onClose: () => void;
}) {
  const can = useCan();
  const canMoney = can('global.view_money');
  const [messages, setMessages] = useState<DefMsg[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [confirmingDraft, setConfirmingDraft] = useState<string | null>(null);
  const [attachInvoice, setAttachInvoice] = useState(false);
  const [invoice, setInvoice] = useState<InvoiceSummary | null>(null);
  const [sendingDefense, setSendingDefense] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  const loadInvoice = useCallback(async () => {
    try {
      const res: any = await api.get(
        `/ip-complaints/${caseId}/defender/invoice?seller_id=${sellerId}`
      );
      if (res.success) setInvoice(res.data.invoice ?? null);
    } catch {
      /* sin factura no bloquea */
    }
  }, [caseId, sellerId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await api.get(`/ip-complaints/${caseId}/defender?seller_id=${sellerId}`);
      if (res.success) setMessages(res.data.messages || []);
    } catch {
      /* historial vacío no bloquea */
    } finally {
      setLoading(false);
    }
  }, [caseId, sellerId]);

  useEffect(() => {
    load();
    loadInvoice();
  }, [load, loadInvoice]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || sending) return;
    setSending(true);
    setError(null);
    setMessages((m) => [...m, { role: 'user', content: msg }]);
    setInput('');
    try {
      const res: any = await api.post(`/ip-complaints/${caseId}/defender`, {
        seller_id: sellerId,
        message: msg,
      });
      if (res.success) {
        setMessages((m) => [
          ...m,
          { role: 'assistant', content: res.data.reply, saved_playbook: res.data.saved },
        ]);
        loadInvoice(); // el Defensor pudo haberla generado en este turno
      } else {
        setError(res.error || 'El Defensor no pudo responder');
      }
    } catch (err: any) {
      setError(err?.message || 'Error de red');
    } finally {
      setSending(false);
    }
  };

  const openConfirm = (draft: string, recommended: boolean) => {
    setAttachInvoice(recommended);
    setConfirmingDraft(draft);
  };

  const sendDefense = async (draft: string) => {
    setSendingDefense(true);
    setError(null);
    try {
      const res: any = await api.post(`/ip-complaints/${caseId}/defender/send`, {
        seller_id: sellerId,
        text: draft,
        attach_invoice: attachInvoice,
      });
      if (res.success) {
        setConfirmingDraft(null);
        const inv = res.data?.invoice;
        setMessages((m) => [
          ...m,
          {
            role: 'assistant',
            content:
              `✅ Defensa enviada y registrada en el caso${inv ? ` junto con la factura ${inv.invoiceNumber}` : ''}. ` +
              'Ahora el denunciante tiene 4 días para aceptarla o rechazarla; si no contesta, el aviso se reactiva solo.',
          },
        ]);
        loadInvoice();
        onSent();
      } else {
        setError(res.error || 'No se pudo enviar la defensa');
      }
    } catch (err: any) {
      setError(err?.message || 'Error de red');
    } finally {
      setSendingDefense(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex justify-end bg-black/50" onClick={onClose}>
      <div
        className="w-full max-w-md h-full bg-[var(--color-bg)] border-l border-[var(--color-border)] shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-4 py-3 border-b border-[var(--color-border)] flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Scale size={18} className="text-amber-500 shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-bold text-[var(--color-text)]">El Defensor</div>
              <div
                className="text-[10px] text-[var(--color-text-muted)] truncate"
                title={caseTitle}
              >
                Caso {caseId} · {caseTitle}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
          >
            <X size={18} />
          </button>
        </div>

        {/* Mensajes */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-[var(--color-text-muted)] py-4">
              <Loader2 size={14} className="animate-spin" /> Cargando expediente…
            </div>
          ) : messages.length === 0 ? (
            <div className="text-xs text-[var(--color-text-muted)] py-6 text-center space-y-2">
              <p>
                El Defensor es tu abogado interno para esta denuncia: ya tiene el expediente
                completo (el caso, el aviso, los textos y el historial de denuncias).
              </p>
              <p>
                Pedile un <strong>dictamen</strong> (&ldquo;¿la peleamos o la retiramos?&rdquo;) o
                que <strong>redacte la defensa</strong>. Nada se envía sin tu confirmación.
              </p>
            </div>
          ) : (
            messages.map((m, i) => {
              const extracted =
                m.role === 'assistant'
                  ? extractDefenseDraft(m.content)
                  : { draft: null, missingEnd: false };
              const draft = extracted.draft;
              const recommendsInvoice = m.role === 'assistant' && wantsInvoiceAttached(m.content);
              return (
                <div
                  key={m.id ?? `local-${i}`}
                  className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}
                >
                  <div
                    className={`max-w-[88%] rounded-xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                      m.role === 'user'
                        ? 'bg-amber-600 text-white'
                        : 'bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-[var(--color-text)]'
                    }`}
                  >
                    {m.content}
                    {(m.saved_playbook?.length ?? 0) > 0 && (
                      <div className="mt-2 space-y-1">
                        {m.saved_playbook!.map((s, j) => (
                          <div
                            key={j}
                            className="flex items-start gap-1.5 text-[11px] px-2 py-1 rounded bg-emerald-500/10 border border-emerald-500/25 text-emerald-500"
                          >
                            <BookMarked size={12} className="shrink-0 mt-0.5" />
                            <span>
                              {s.accion === 'aviso_corregido'
                                ? `Aviso corregido (${s.id}): ${s.text}`
                                : `Cuaderno ${s.accion} (${s.id}): ${s.text}`}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    {extracted.missingEnd && (
                      <div className="mt-2 text-[11px] px-2 py-1.5 rounded bg-amber-500/10 border border-amber-500/30 text-amber-500">
                        Este borrador no tiene la marca de cierre, así que no se puede enviar
                        (protección para que nunca viajen los comentarios internos). Pedile al
                        Defensor: &ldquo;cerrá el borrador con FIN DEL BORRADOR&rdquo;.
                      </div>
                    )}
                    {draft &&
                      (confirmingDraft === draft ? (
                        <div className="mt-2 space-y-2">
                          <div className="text-[11px] font-semibold text-red-400">
                            Esto es EXACTAMENTE lo que va a recibir el denunciante (nada más,
                            nada menos). Revisalo antes de confirmar:
                          </div>
                          <div className="max-h-56 overflow-y-auto text-[11px] whitespace-pre-wrap px-2 py-1.5 rounded bg-[var(--color-bg)] border border-red-500/40 text-[var(--color-text)]">
                            {draft}
                          </div>
                          <label className="flex items-start gap-2 text-[11px] px-2 py-1.5 rounded bg-sky-500/10 border border-sky-500/30 text-[var(--color-text)] cursor-pointer">
                            <input
                              type="checkbox"
                              checked={attachInvoice}
                              onChange={(e) => setAttachInvoice(e.target.checked)}
                              className="mt-0.5"
                            />
                            <span className="flex-1">
                              <span className="font-semibold flex items-center gap-1">
                                <FileText size={12} /> Adjuntar la factura de compra
                                {recommendsInvoice && (
                                  <span className="text-sky-500">(el Defensor la recomienda)</span>
                                )}
                              </span>
                              {attachInvoice &&
                                (invoice ? (
                                  <span className="block text-[var(--color-text-muted)]">
                                    Va unida a la carta en un solo PDF: factura {invoice.invoiceNumber} ·{' '}
                                    {invoice.fromCompany} → {invoice.toCompany} · {invoice.quantity} u
                                    {invoice.totalAmount != null && canMoney
                                      ? ` · USD ${invoice.totalAmount.toFixed(2)}`
                                      : ''}{' '}
                                    ·{' '}
                                    <a
                                      href={invoice.downloadPath}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="underline text-sky-500"
                                    >
                                      ver PDF
                                    </a>
                                    {!invoice.pdfReady && (
                                      <span className="text-red-400"> · ⚠️ PDF no disponible</span>
                                    )}
                                  </span>
                                ) : (
                                  <span className="block text-[var(--color-text-muted)]">
                                    Todavía no hay factura para este aviso: se genera en el momento
                                    del envío y va unida a la carta. Si no se puede generar, el
                                    envío se frena y te avisa.
                                  </span>
                                ))}
                            </span>
                          </label>
                          <div className="flex items-center gap-2">
                            <Can cap="orders.defender">
                            <button
                              onClick={() => sendDefense(draft)}
                              disabled={sendingDefense}
                              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-red-600 text-white text-xs font-semibold hover:bg-red-700 disabled:opacity-50"
                            >
                              {sendingDefense ? (
                                <Loader2 size={13} className="animate-spin" />
                              ) : (
                                <ShieldCheck size={13} />
                              )}
                              Confirmar envío a Mercado Libre
                            </button>
                            </Can>
                            <button
                              onClick={() => setConfirmingDraft(null)}
                              className="text-xs text-[var(--color-text-muted)] px-2 py-1.5 hover:text-[var(--color-text)]"
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <Can cap="orders.defender">
                        <button
                          onClick={() => openConfirm(draft, recommendsInvoice)}
                          className="mt-2 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-green-500/15 text-green-500 text-xs font-medium hover:bg-green-500/25"
                        >
                          <ShieldCheck size={13} /> Enviar esta defensa
                          {recommendsInvoice && (
                            <span className="flex items-center gap-1 text-sky-500">
                              <FileText size={12} /> + factura
                            </span>
                          )}
                        </button>
                        </Can>
                      ))}
                  </div>
                </div>
              );
            })
          )}
          {sending && (
            <div className="flex items-center gap-2 text-xs text-[var(--color-text-muted)]">
              <Loader2 size={13} className="animate-spin" /> El Defensor está analizando el
              expediente…
            </div>
          )}
          {error && <div className="text-xs text-red-400">{error}</div>}
          <div ref={endRef} />
        </div>

        {/* Input */}
        <div className="px-4 py-3 border-t border-[var(--color-border)] flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={2}
            placeholder='Ej: "Dame tu dictamen de este caso" o "Redactá la defensa"'
            className="flex-1 resize-none px-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)] placeholder:text-[var(--color-text-muted)]"
          />
          <button
            onClick={send}
            disabled={!input.trim() || sending}
            className="h-10 w-10 flex items-center justify-center rounded-lg bg-amber-600 text-white hover:opacity-90 disabled:opacity-50 shrink-0"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
        </div>
      </div>
    </div>
  );
}
